import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { ChatContext } from './chat-context-types';

// Feature-provided renderers for the chat composer's slots. Features register
// these at the app composition root (see app/chat-slot-registry.ts), so the
// chat module never imports feature code — that import direction would create
// a module cycle (features import the chat barrel for tile chrome).
export type ChatSlotRenderers = {
    readonly idleSlot?: (context: ChatContext) => ReactNode;
    readonly prompt?: (context: ChatContext) => ReactNode;
};

const ChatSlotRenderersContext = createContext<readonly ChatSlotRenderers[]>([]);

export const ChatSlotRenderersProvider: FunctionComponent<
    PropsWithChildren<{ readonly renderers: readonly ChatSlotRenderers[] }>
> = ({ children, renderers }) => (
    <ChatSlotRenderersContext.Provider value={renderers}>{children}</ChatSlotRenderersContext.Provider>
);

export const useChatSlotContent = (slot: keyof ChatSlotRenderers, context: ChatContext | null): ReactNode => {
    const renderers = useContext(ChatSlotRenderersContext);

    if (context === null) {
        return null;
    }

    for (const renderer of renderers) {
        const content = renderer[slot]?.(context);

        if (content !== undefined && content !== null) {
            return content;
        }
    }

    return null;
};
